import { api } from "./client";
import type { DetailRow } from "./inv-depth";

/*
 * Inventory polish, wave 6 (server/inv-polish*.js, inv-rescope.js, inv-doc-tracking.js): the serial and batch picker of the invoice, bill,
 * credit note and vendor credit forms, goods in a bin reserved for a sales order or invoice, the guided valuation change and a Stock on
 * Hand account per location. Quantities and money are decimal strings from the server. Docs: docs/implemented-inv-polish.md.
 */

const get = <T,>(path: string, query?: Record<string, string | null | undefined>) => {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query ?? {})) if (v) qs.set(k, v);
  const s = qs.toString();
  return api<T>("GET", s ? `${path}?${s}` : path);
};
const post = <T,>(path: string, body: unknown = {}) => api<T>("POST", path, body);

type Qty = string;
const INV = "/admin/inventory";

/** What one line of a form can pick: serials and batches at the location (on the day and still there), bins, and what an invoice took. */
export type PickOptions = {
  item_id: string;
  item_name: string;
  unit: string | null;
  mode: "serial" | "batch" | "none";
  required: boolean;
  tracked: boolean;
  bins: { id: string; code: string; name: string | null }[];
  serials: { serial_no: string; bin_id: string | null; bin: string | null; batch: string | null }[];
  batches: { batch_no: string; mfg_date: string | null; expiry_date: string | null; quantity: Qty; bins: { bin_id: string | null; bin: string | null; quantity: Qty }[] }[];
  sold: { serial_no: string | null; batch_no: string | null; quantity: Qty }[];
};

export type ReservationStatus = "holding" | "used" | "partly_used" | "lapsed" | "released";
export type Reservation = {
  id: string;
  number: string;
  date: string;
  doc_id: string;
  doc_kind: "sales_order" | "invoice" | null;
  doc_label: string;
  doc_number: string | null;
  item_id: string;
  item_name: string;
  location_id: string;
  location_name: string | null;
  bin_id: string;
  bin: string | null;
  batch: string | null;
  serial: string | null;
  quantity: Qty;
  used: Qty;
  holding: Qty;
  status: ReservationStatus;
  status_label: string;
  notes: string | null;
  created_by_name: string | null;
  created_at: string;
  released_at: string | null;
  released_by_name: string | null;
  can_release: boolean;
  /** Wave 9: a reservation on a package (doc_id is its invoice). */
  package_id?: string | null;
  package_number?: string | null;
};
/** Wave 9: kind "package" (with invoice_id and invoice_number) when stock leaves on shipment; issued invoices waiting to ship too. */
export type ReservableDoc = { id: string; kind: "sales_order" | "invoice" | "package"; number: string | null; status: string; customer_name: string; issue_date: string; location_id: string | null; doc_label: string; invoice_id?: string; invoice_number?: string | null; lines: { item_id: string; item_name: string; quantity: Qty }[] };
export type ReservationIn = { doc_id?: string; package_id?: string; item_id: string; bin_id: string; batch_no?: string; serial_no?: string; quantity?: string; date?: string; notes?: string };

export type Scope = "item" | "location";
export type RescopePreview = {
  date: string;
  from_scope: Scope;
  to_scope: Scope;
  from_label: string;
  to_label: string;
  /** carry_*: wave 9, a shortfall carried into the new valuation at the last cost (what it is carried at, and the difference it posts). */
  rows: { item_id: string; item_name: string; unit: string | null; location_id: string | null; location_name: string | null; quantity: Qty; value: string; unit_cost: string; carry_unit_cost?: string; carry_difference?: string }[];
  problems: string[];
  can: boolean;
  default_account_id: string | null;
  /** Wave 8: each thing in the way, with what clears it. settle: a transfer the change can receive on settle_date. */
  blockers?: RescopeBlocker[];
  can_settle?: boolean;
  settle_date?: string;
  /** Previewed with settle: the transfers the change would receive. */
  settling?: { id: string; number: string; received_date: string }[];
  /** Wave 9: what the guided step does about locations below zero. */
  negative?: RescopeNegative | null;
  carry?: { item_id: string; item_name: string; location_id: string; location_name: string | null; quantity: Qty; unit_cost: string }[];
  receipts?: RescopeReceipt[];
};
export type RescopeNegative = "receipt" | "last_cost";
export type RescopeReceipt = { id: string; number: string; location_id: string; location_name: string | null; lines: { item_id: string; item_name: string | null; quantity: Qty; value: string | null }[] };
export type RescopeBlocker = {
  kind: "general" | "in_transit" | "below_zero";
  text: string;
  action: string | null;
  settle?: boolean;
  transfer_id?: string;
  transfer_number?: string;
  sent_date?: string;
  received_date?: string | null;
  from_location_name?: string;
  to_location_name?: string;
  lines?: { item_id: string; item_name: string; quantity: Qty }[];
  item_id?: string;
  item_name?: string;
  location_id?: string;
  location_name?: string;
  quantity?: Qty;
  /** Wave 9: below zero, the last cost a unit. */
  last_cost?: string;
};
export type Rescope = {
  id: string;
  number: string;
  date: string;
  from_scope: Scope;
  to_scope: Scope;
  label: string;
  account_id: string;
  account_name: string | null;
  status: "done" | "void";
  notes: string | null;
  difference: string;
  lines: { item_id: string; item_name: string | null; location_id: string | null; location_name: string | null; unit_cost: string }[];
  created_by_name: string | null;
  created_at: string;
  void_reason: string | null;
  can_void: boolean;
  /** The transfers the change received (wave 8: the day before it; wave 9: its own day, first thing). */
  settled_transfers?: { id: string; number: string; received_date: string }[];
  /** Wave 9: the adjustments it recorded for locations below zero, and the shortfalls it carried at the last cost. */
  receipts?: RescopeReceipt[];
  carried?: { item_id: string; item_name: string; location_id: string; location_name: string | null; quantity: Qty; unit_cost: string }[];
};
export type RescopeIn = { to: Scope; date: string; account_id?: string | null; notes?: string; settle_transfers?: boolean; negative?: RescopeNegative; lines: { item_id: string; location_id?: string | null; unit_cost: string }[] };
export type LocationAccounts = {
  on: boolean;
  from: string | null;
  transit_account: string | null;
  locations: { location_id: string; location_name: string; status: string; account_id: string | null; account: string | null }[];
  missing: number;
  scope_today: Scope;
};

export const invPolish = {
  pick: (q: { item_id: string; location_id?: string | null; date?: string | null; invoice_id?: string | null }) => get<PickOptions>(`${INV}/meta/pick`, q),
  lineTracking: (doc: "invoice" | "bill", id: string) => get<{ lines: Record<string, DetailRow[]> }>(`${INV}/meta/line-tracking`, { doc, id }),

  reservations: (q: { doc_id?: string; item_id?: string } = {}) => get<Reservation[]>(`${INV}/reservations`, q),
  reservableDocs: () => get<ReservableDoc[]>(`${INV}/reservations/docs`),
  reserve: (body: ReservationIn) => post<Reservation>(`${INV}/reservations`, body),
  release: (id: string, reason?: string) => post<Reservation>(`${INV}/reservations/${id}/release`, { reason }),

  rescopePreview: (to: Scope, date: string, settle = false, negative: RescopeNegative | null = null) => get<RescopePreview>(`${INV}/settings/rescope`, { to, date, settle: settle ? "1" : null, negative }),
  rescope: (body: RescopeIn) => post<Rescope>(`${INV}/settings/rescope`, body),
  rescopes: () => get<Rescope[]>(`${INV}/settings/rescopes`),
  voidRescope: (id: string, reason: string) => post<Rescope>(`${INV}/settings/rescopes/${id}/void`, { reason }),
  locationAccounts: () => get<LocationAccounts>(`${INV}/settings/location-accounts`),
  saveLocationAccounts: (body: { from?: string } | { off: true }) => post<LocationAccounts>(`${INV}/settings/location-accounts`, body),
};
